import { create } from "zustand";

export type Tab = "badger" | "activites" | "timesheet" | "profil";
export type ToastVariant = "in" | "out" | "pause" | "info" | "error";

export interface Toast {
  id: string;
  variant: ToastVariant;
  title: string;
  subtitle?: string;
}

export interface SettingsState {
  geolocation: boolean;
  notifications: boolean;
  nfcBorne: boolean;
}

export type BrandPreset =
  "#2f5fd8" | "#1b4ed8" | "#7c56f5" | "#0d8a8a" | "#1f1f25";

export type ActivityIcon =
  "briefcase" | "tools" | "tractor" | "truck" | "coffee" | "list";

export interface ActivityEntry {
  code: string;
  label: string;
  sub: string;
  ic: ActivityIcon;
  custom?: boolean;
  description?: string;
  requires_worker_note?: boolean;
}

export type ActivityViewMode = "list" | "timeline";

export const ACTIVITY_VIEW_TABS: { id: ActivityViewMode; label: string }[] = [
  { id: "list", label: "Liste" },
  { id: "timeline", label: "Timeline" },
];

const CUSTOM_ACTIVITIES_KEY = "senacle-workforce.customActivities";
const SETTINGS_KEY = "senacle-workforce.settings";
const HOME_VIEW_MODE_KEY = "senacle-workforce.homeViewMode";

const DEFAULT_SETTINGS: SettingsState = {
  geolocation: true,
  notifications: true,
  nfcBorne: false,
};

function loadSettings(): SettingsState {
  if (typeof localStorage === "undefined") return DEFAULT_SETTINGS;
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<SettingsState>;
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function persistSettings(s: SettingsState): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    // ignore
  }
}

function loadViewMode(key: string): ActivityViewMode {
  if (typeof localStorage === "undefined") return "list";
  try {
    const raw = localStorage.getItem(key);
    return raw === "timeline" ? "timeline" : "list";
  } catch {
    return "list";
  }
}

function persistViewMode(key: string, v: ActivityViewMode): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(key, v);
  } catch {
    // ignore
  }
}

function loadCustomActivities(): ActivityEntry[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(CUSTOM_ACTIVITIES_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is ActivityEntry =>
        !!e &&
        typeof e === "object" &&
        typeof (e as ActivityEntry).code === "string" &&
        typeof (e as ActivityEntry).label === "string",
    );
  } catch {
    return [];
  }
}

function persistCustomActivities(list: ActivityEntry[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(CUSTOM_ACTIVITIES_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

export interface UiState {
  tab: Tab;
  setTab: (t: Tab) => void;
  online: boolean;
  setOnline: (v: boolean) => void;
  sheetOpen: boolean;
  openSheet: () => void;
  closeSheet: () => void;
  noteEditTarget: {
    eventId: string;
    label: string;
    current: string;
  } | null;
  openNoteEdit: (t: {
    eventId: string;
    label: string;
    current: string;
  }) => void;
  closeNoteEdit: () => void;
  toasts: Toast[];
  pushToast: (t: Omit<Toast, "id">) => void;
  popToast: (id: string) => void;
  settings: SettingsState;
  setSetting: <K extends keyof SettingsState>(
    k: K,
    v: SettingsState[K],
  ) => void;
  brand: BrandPreset;
  setBrand: (b: BrandPreset) => void;
  customActivities: ActivityEntry[];
  addCustomActivity: (a: ActivityEntry) => void;
  removeCustomActivity: (code: string) => void;
  homeViewMode: ActivityViewMode;
  setHomeViewMode: (v: ActivityViewMode) => void;
}

const TOAST_TTL_MS = 3500;

function generateId(): string {
  const c: { randomUUID?: () => string } | undefined =
    typeof globalThis !== "undefined"
      ? (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
      : undefined;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}

export const useUiStore = create<UiState>()((set, get) => ({
  tab: "badger",
  setTab: (t) => set({ tab: t }),

  online: true,
  setOnline: (v) => set({ online: v }),

  sheetOpen: false,
  openSheet: () => set({ sheetOpen: true }),
  closeSheet: () => set({ sheetOpen: false }),

  noteEditTarget: null,
  openNoteEdit: (t) => set({ noteEditTarget: t }),
  closeNoteEdit: () => set({ noteEditTarget: null }),

  toasts: [],
  pushToast: (t) => {
    const id = generateId();
    set((state) => ({ toasts: [...state.toasts, { id, ...t }] }));
    setTimeout(() => {
      get().popToast(id);
    }, TOAST_TTL_MS);
  },
  popToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((x) => x.id !== id) })),

  settings: loadSettings(),
  setSetting: (k, v) =>
    set((state) => {
      const next = { ...state.settings, [k]: v };
      persistSettings(next);
      return { settings: next };
    }),

  brand: "#2f5fd8",
  setBrand: (b) => set({ brand: b }),

  customActivities: loadCustomActivities(),
  addCustomActivity: (a) =>
    set((state) => {
      const filtered = state.customActivities.filter((x) => x.code !== a.code);
      const next = [...filtered, { ...a, custom: true }];
      persistCustomActivities(next);
      return { customActivities: next };
    }),
  removeCustomActivity: (code) =>
    set((state) => {
      const next = state.customActivities.filter((x) => x.code !== code);
      persistCustomActivities(next);
      return { customActivities: next };
    }),

  homeViewMode: loadViewMode(HOME_VIEW_MODE_KEY),
  setHomeViewMode: (v) => {
    persistViewMode(HOME_VIEW_MODE_KEY, v);
    set({ homeViewMode: v });
  },
}));
